import React, { useState, useEffect } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Link, useParams, useNavigate } from "react-router-dom";
import authService from "../services/discordAuth";
import { trackEvent } from "../utils/analytics";
import usePageTitle from "../hooks/usePageTitle";
import SuccessModal from "../components/SuccessModal";
import AdminActionModal from "../components/AdminActionModal";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, Filler } from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import "./ProfilePage.css";
import "../components/ReportModal.css";

// Registrar componentes do Chart.js
ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, Filler);

function ProfilePage() {
  const { t, i18n } = useTranslation();
  const { userId: urlUserId } = useParams(); // Captura userId da URL
  const navigate = useNavigate();

  usePageTitle(t("profile_page.title"));

  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [bio, setBio] = useState(t("profile_page.sidebar.bio_default"));
  const [recentVotes, setRecentVotes] = useState([]);
  const [myBots, setMyBots] = useState([]);
  const [activities, setActivities] = useState([]);
  const [activityPage, setActivityPage] = useState(0);
  const [reportsPage, setReportsPage] = useState(0);
  const [userReports, setUserReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [showBadges, setShowBadges] = useState(true); // Controla visibilidade das badges
  const [showActivity, setShowActivity] = useState(true); // Controla visibilidade da aba atividade
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDescription, setReportDescription] = useState("");
  const [shouldReopenReportModal, setShouldReopenReportModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({ title: "", message: "" });
  const [showDeleteBotModal, setShowDeleteBotModal] = useState(false);
  const [botToDelete, setBotToDelete] = useState(null);
  const [showAdminActionModal, setShowAdminActionModal] = useState(false);
  const [adminActionData, setAdminActionData] = useState(null);
  const [adminActionType, setAdminActionType] = useState(null); // 'ban' ou 'reject'
  const [stats, setStats] = useState({
    bots: 0,
    votes: 0,
    reviews: 0,
    memberSince: "",
  });
  const [selectedBotId, setSelectedBotId] = useState("");
  const [apiUsage, setApiUsage] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [usagePage, setUsagePage] = useState(1);
  const [chartMode, setChartMode] = useState("bar"); // 'bar' ou 'line'

  // Função para calcular a data de criação da conta do Discord baseada no ID
  const getAccountCreationDate = (userId) => {
    if (!userId) return t("profile_page.sidebar.unknown");

    // Discord Snowflake: primeiros 42 bits são timestamp em ms desde Discord Epoch (2015-01-01)
    const DISCORD_EPOCH = 1420070400000;
    // eslint-disable-next-line no-undef
    const timestamp = Number(BigInt(userId) >> 22n) + DISCORD_EPOCH;
    return new Date(timestamp).toLocaleDateString(i18n.language === "pt" ? "pt-BR" : "en-US");
  };

  // Componente de Gráfico usando Chart.js
  const StatsChart = ({ data, type = "bar" }) => {
    if (!data || data.length === 0) return null;

    // Ordenar cronologicamente e pegar os últimos 14 dias
    const sortedData = [...data].sort((a, b) => new Date(a.date) - new Date(b.date)).slice(-14);

    const labels = sortedData.map((d) => {
      const date = new Date(d.date);
      return date.toLocaleDateString(i18n.language === "pt" ? "pt-BR" : "en-US", {
        day: "2-digit",
        month: "2-digit",
      });
    });

    const callsData = sortedData.map((d) => d.calls);

    const chartData = {
      labels,
      datasets: [
        {
          label: t("profile_page.stats.history.chart_label"),
          data: callsData,
          backgroundColor: type === "bar" ? "rgba(88, 101, 242, 0.6)" : "rgba(88, 101, 242, 0.1)",
          borderColor: "rgba(88, 101, 242, 1)",
          borderWidth: type === "bar" ? 0 : 3,
          borderRadius: type === "bar" ? 8 : 0,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: "#fff",
          pointBorderColor: "rgba(88, 101, 242, 1)",
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: "rgba(17, 24, 39, 0.95)",
          titleColor: "#9ca3af",
          bodyColor: "#fff",
          padding: 12,
          cornerRadius: 8,
          displayColors: false,
          borderColor: "rgba(88, 101, 242, 0.3)",
          borderWidth: 1,
          callbacks: {
            label: (context) => ` ${context.parsed.y} ${t("profile_page.stats.api_calls_chart")}`,
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: "rgba(255, 255, 255, 0.05)",
            drawBorder: false,
          },
          ticks: {
            color: "#6b7280",
            font: { size: 11 },
            maxTicksLimit: 5,
          },
        },
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: "#6b7280",
            font: { size: 11 },
          },
        },
      },
      animation: {
        duration: 1000,
        easing: "easeOutQuart",
      },
    };

    return <div style={{ height: "250px", width: "100%" }}>{type === "bar" ? <Bar data={chartData} options={options} /> : <Line data={chartData} options={options} />}</div>;
  };

  // Função para obter avatar do usuário
  const getUserAvatar = (userId, avatar) => {
    if (avatar) {
      return `https://cdn.discordapp.com/avatars/${userId}/${avatar}${avatar.startsWith("a_") ? ".gif" : ".png"}?size=256`;
    }
    return `https://cdn.discordapp.com/embed/avatars/${parseInt(userId || "0") % 5}.png`;
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
      const loggedUser = authService.isAuthenticated() ? authService.getUser() : null;

      // Determina qual userId usar: da URL ou do usuário logado
      const targetUserId = urlUserId || loggedUser?.id;

      if (!targetUserId) {
        setLoading(false);
        return;
      }

      // Verifica se é o próprio perfil
      const isOwn = loggedUser && loggedUser.id === targetUserId;
      setIsOwnProfile(isOwn);

      try {
        // Se for visualizar outro perfil, buscar dados básicos do usuário
        if (!isOwn && urlUserId) {
          // Buscar do banco de dados
          const dbUserResponse = await fetch(`${BACKEND_URL}/api/user/info?userId=${urlUserId}`);
          if (dbUserResponse.ok) {
            const dbUserData = await dbUserResponse.json();
            setUser(dbUserData.user);
            // Configurar visibilidade baseado nas preferências do usuário
            setShowBadges(dbUserData.user.showBadges !== undefined ? dbUserData.user.showBadges : true);
            setShowActivity(dbUserData.user.showActivity !== undefined ? dbUserData.user.showActivity : true);
          } else {
            // Fallback com dados mínimos
            setUser({
              id: urlUserId,
              username: t("profile_page.sidebar.fallback_username"),
              discriminator: "0000",
              avatar: null,
            });
            setShowBadges(true);
            setShowActivity(true);
          }
        } else {
          // Usar dados do usuário logado - sempre mostra tudo no próprio perfil
          setUser(loggedUser);
          setShowBadges(true);
          setShowActivity(true);
        }

        // Calcula "Membro Desde" baseado no Discord ID
        const memberSince = getAccountCreationDate(targetUserId);
        setStats((prev) => ({ ...prev, memberSince }));

        // Buscar perfil (badges do Discord) - apenas se for o próprio perfil e estiver logado
        if (isOwn && loggedUser) {
          const token = authService.getAccessToken();
          const profileResponse = await fetch(`${BACKEND_URL}/api/user/profile`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (profileResponse.ok) {
            const profileData = await profileResponse.json();
            setUserProfile(profileData.data);
          }
        }

        // Buscar estatísticas dos cards
        const statsResponse = await fetch(`${BACKEND_URL}/api/user/stats?userId=${targetUserId}`);
        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          setStats((prev) => ({
            ...prev,
            bots: statsData.stats.botsCreated,
            votes: statsData.stats.votesGiven,
            reviews: statsData.stats.reviewsGiven,
          }));
        }

        // Buscar bio do usuário
        const bioResponse = await fetch(`${BACKEND_URL}/api/user/bio?userId=${targetUserId}`);
        if (bioResponse.ok) {
          const bioData = await bioResponse.json();
          setBio(bioData.bio);
        }

        // Buscar votos recentes
        const votesResponse = await fetch(`${BACKEND_URL}/api/user/recent-votes?userId=${targetUserId}&limit=3`);
        if (votesResponse.ok) {
          const votesData = await votesResponse.json();
          setRecentVotes(votesData.votes);
        }

        // Buscar bots do usuário
        const viewerId = loggedUser?.id || null;
        const botsUrl = viewerId ? `${BACKEND_URL}/api/user/my-bots?userId=${targetUserId}&viewerId=${viewerId}` : `${BACKEND_URL}/api/user/my-bots?userId=${targetUserId}`;

        const botsResponse = await fetch(botsUrl);
        if (botsResponse.ok) {
          const botsData = await botsResponse.json();
          setMyBots(botsData.bots);
        }

        // Buscar atividades do usuário
        const activityResponse = await fetch(`${BACKEND_URL}/api/user/activity?userId=${targetUserId}&limit=10`);
        if (activityResponse.ok) {
          const activityData = await activityResponse.json();
          setActivities(activityData.activities);
        }

        // Buscar denúncias do usuário (apenas se for o próprio perfil)
        if (isOwn) {
          const token = authService.getAccessToken();
          const reportsResponse = await fetch(`${BACKEND_URL}/api/user/reports`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          if (reportsResponse.ok) {
            const reportsData = await reportsResponse.json();
            setUserReports(reportsData.reports);
          }
        }
      } catch (error) {
        console.error("Erro ao buscar dados do perfil:", error);
      }

      setLoading(false);
    };

    fetchUserProfile();
  }, [urlUserId]); // Recarrega quando o userId da URL mudar

  // Buscar uso da API quando o bot selecionado mudar
  useEffect(() => {
    const fetchApiUsage = async () => {
      if (!selectedBotId || activeTab !== "stats") return;

      setStatsLoading(true);
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
        const token = authService.getAccessToken();

        const response = await fetch(`${BACKEND_URL}/api/user/api-usage/${selectedBotId}?page=${usagePage}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setApiUsage(data.data);
          }
        }
      } catch (error) {
        console.error("Erro ao buscar uso da API:", error);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchApiUsage();
  }, [selectedBotId, activeTab, usagePage]);

  // Selecionar o primeiro bot automaticamente ao mudar para a aba de stats se nenhum estiver selecionado
  useEffect(() => {
    if (activeTab === "stats" && !selectedBotId && myBots.length > 0) {
      setSelectedBotId(myBots[0].id);
    }
  }, [activeTab, myBots, selectedBotId]);

  // Função para enviar denúncia
  const handleSubmitReport = async () => {
    try {
      const token = authService.getAccessToken();
      if (!token) {
        setModalConfig({
          title: t("profile_page.modals.feedback.attention"),
          message: t("profile_page.modals.feedback.auth_error"),
        });
        setShowSuccessModal(true);
        setShowReportModal(false);
        return;
      }

      if (!reportReason || !reportDescription) {
        setShowReportModal(false);
        setShouldReopenReportModal(true);
        setModalConfig({
          title: t("profile_page.modals.feedback.attention"),
          message: t("profile_page.modals.feedback.fill_all"),
        });
        setShowSuccessModal(true);
        return;
      }

      if (reportDescription.length < 10) {
        setShowReportModal(false);
        setShouldReopenReportModal(true);
        setModalConfig({
          title: t("profile_page.modals.feedback.attention"),
          message: t("profile_page.modals.feedback.desc_min"),
        });
        setShowSuccessModal(true);
        return;
      }

      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
      const targetUserId = urlUserId || user.id;

      const response = await fetch(`${BACKEND_URL}/api/reports`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          targetType: "user",
          targetId: targetUserId,
          reason: reportReason,
          description: reportDescription,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        trackEvent("user_submit_report", {
          target_type: "user",
          target_id: targetUserId,
          reason: reportReason,
        });
        setModalConfig({
          title: t("profile_page.modals.feedback.report_success_title"),
          message: t("profile_page.modals.feedback.report_success_message"),
        });
        setShowSuccessModal(true);
        setShowReportModal(false);
        setReportReason("");
        setReportDescription("");
      } else {
        setShowReportModal(false);
        setModalConfig({
          title: t("profile_page.modals.feedback.ops"),
          message: data.error || t("profile_page.modals.feedback.report_error"),
        });
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error("Erro ao enviar denúncia:", error);
      setShowReportModal(false);
      setModalConfig({
        title: t("profile_page.modals.feedback.error"),
        message: t("profile_page.modals.feedback.report_generic_error"),
      });
      setShowSuccessModal(true);
    }
  };

  // Função para buscar e exibir motivo de banimento
  const handleShowBanReason = async (bot) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

      const response = await fetch(`${BACKEND_URL}/api/bots/${bot.id}/admin-action`);

      if (response.ok) {
        const data = await response.json();
        setAdminActionData(data.action);
        setAdminActionType("ban");
        setShowAdminActionModal(true);
      } else {
        setModalConfig({
          title: t("profile_page.modals.feedback.error"),
          message: t("profile_page.modals.feedback.ban_info_error"),
        });
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error("Erro ao buscar motivo de banimento:", error);
      setModalConfig({
        title: t("profile_page.modals.feedback.error"),
        message: t("profile_page.modals.feedback.fetch_error"),
      });
      setShowSuccessModal(true);
    }
  };

  // Função para buscar e exibir motivo de rejeição
  const handleShowRejectionReason = async (bot) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

      const response = await fetch(`${BACKEND_URL}/api/bots/${bot.id}/admin-action`);

      if (response.ok) {
        const data = await response.json();
        setAdminActionData(data.action);
        setAdminActionType("reject");
        setShowAdminActionModal(true);
      } else {
        setModalConfig({
          title: t("profile_page.modals.feedback.error"),
          message: t("profile_page.modals.feedback.reject_info_error"),
        });
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error("Erro ao buscar motivo de rejeição:", error);
      setModalConfig({
        title: t("profile_page.modals.feedback.error"),
        message: t("profile_page.modals.feedback.fetch_error"),
      });
      setShowSuccessModal(true);
    }
  };

  // Função para fechar o modal de sucesso e reabrir o modal de denúncia se necessário
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    if (shouldReopenReportModal) {
      setShouldReopenReportModal(false);
      setShowReportModal(true);
    }
  };

  // Função para abrir modal de exclusão de bot
  const handleDeleteBot = (bot) => {
    setBotToDelete(bot);
    setShowDeleteBotModal(true);
  };

  // Função para confirmar exclusão do bot
  const confirmDeleteBot = async () => {
    if (!botToDelete) return;

    try {
      const token = authService.getAccessToken();
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

      const response = await fetch(`${BACKEND_URL}/api/user/bots/${botToDelete.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        trackEvent("user_delete_bot", {
          bot_id: botToDelete.id,
          bot_name: botToDelete.name,
        });
        // Remove o bot da lista local
        setMyBots((prev) => prev.filter((b) => b.id !== botToDelete.id));

        // Atualiza contador de bots
        setStats((prev) => ({
          ...prev,
          bots: prev.bots - 1,
        }));

        // Mostra mensagem de sucesso
        setModalConfig({
          title: t("profile_page.modals.feedback.delete_success_title"),
          message: t("profile_page.modals.feedback.delete_success_message", {
            name: botToDelete.name,
          }),
        });
        setShowSuccessModal(true);
      } else {
        const data = await response.json();
        setModalConfig({
          title: t("profile_page.modals.feedback.error"),
          message: data.error || t("profile_page.modals.feedback.delete_error"),
        });
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error("Erro ao excluir bot:", error);
      setModalConfig({
        title: t("profile_page.modals.feedback.error"),
        message: t("profile_page.modals.feedback.delete_generic_error"),
      });
      setShowSuccessModal(true);
    } finally {
      setShowDeleteBotModal(false);
      setBotToDelete(null);
    }
  };

  // Função para cancelar exclusão
  const cancelDeleteBot = () => {
    setShowDeleteBotModal(false);
    setBotToDelete(null);
  };

  if (!user) {
    return (
      <div className="profile-page">
        <div className="container">
          <div className="not-logged-in">
            <div className="not-logged-icon">🔒</div>
            <h2>Você precisa estar logado</h2>
            <p>Faça login para ver seu perfil</p>
            <button className="btn btn-primary" onClick={() => authService.login()}>
              Entrar com Discord
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      {/* Hero Section */}
      <div className="profile-hero">
        <div className="profile-hero-background"></div>
        <div className="container">
          <div className="profile-hero-content">
            <div className="profile-avatar-section">
              <img src={getUserAvatar(user.id, user.avatar)} alt={user.username} className="profile-avatar-large" />
              <div className={`profile-status online`}></div>
            </div>
            <div className="profile-info">
              <h1 className="profile-username">{user.username}</h1>
              <p className="profile-id">{t("profile_page.hero.id", { id: user.id })}</p>
              <div className="profile-badges">
                {/* Mistura Badges do Discord e do Site if disponíveis */}
                {showBadges && (
                  <>
                    {/* 1. Badges do Discord ou do Perfil Completo */}
                    {userProfile?.badges && userProfile.badges.length > 0
                      ? userProfile.badges
                          .filter((badge) => badge.name !== "Apoiador Rincko")
                          .map((badge, index) => (
                            <span
                              key={`profile-${index}`}
                              className={`badge ${badge.isSiteBadge ? "badge-site" : "badge-discord"}`}
                              style={{
                                backgroundColor: `${badge.color}20`,
                                color: badge.color,
                                border: `1px solid ${badge.color}40`,
                              }}
                              title={badge.name}
                            >
                              {badge.emoji} {badge.name}
                            </span>
                          ))
                      : // 2. Badges do Banco de Dados (Site) se o perfil completo não estiver carregado (Visualização Pública)
                        user?.site_badges &&
                        user.site_badges.length > 0 &&
                        user.site_badges
                          .filter((badge) => badge.name !== "Apoiador Rincko")
                          .map((badge, index) => (
                            <span
                              key={`site-${index}`}
                              className="badge badge-site"
                              style={{
                                backgroundColor: `${badge.color}20`,
                                color: badge.color,
                                border: `1px solid ${badge.color}40`,
                              }}
                              title={badge.name}
                            >
                              {badge.emoji} {badge.name}
                            </span>
                          ))}

                    {/* 3. Badge Default if nada existir */}
                    {(!userProfile?.badges || userProfile.badges.length === 0) && (!user?.site_badges || user.site_badges.length === 0) && (
                      <span className="badge badge-developer">{t("profile_page.hero.developer_badge")}</span>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="profile-stats-grid">
            <div className="stat-card">
              <div className="stat-icon">🤖</div>
              <div className="stat-value">{stats.bots}</div>
              <div className="stat-label">{t("profile_page.hero.stats.bots")}</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🗳️</div>
              <div className="stat-value">{stats.votes}</div>
              <div className="stat-label">{t("profile_page.hero.stats.votes")}</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⭐</div>
              <div className="stat-value">{stats.reviews}</div>
              <div className="stat-label">{t("profile_page.hero.stats.reviews")}</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📅</div>
              <div className="stat-value">{stats.memberSince}</div>
              <div className="stat-label">{t("profile_page.hero.stats.member_since")}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="profile-tabs">
        <div className="container">
          <div className="tabs-nav">
            <button className={`tab-button ${activeTab === "overview" ? "active" : ""}`} onClick={() => setActiveTab("overview")}>
              {t("profile_page.tabs.overview")}
            </button>
            <button className={`tab-button ${activeTab === "bots" ? "active" : ""}`} onClick={() => setActiveTab("bots")}>
              {isOwnProfile ? t("profile_page.tabs.my_bots", { count: myBots.length }) : t("profile_page.tabs.bots", { count: myBots.length })}
            </button>
            {/* Aba de Atividade - só mostra se showActivity for true */}
            {showActivity && (
              <button className={`tab-button ${activeTab === "activity" ? "active" : ""}`} onClick={() => setActiveTab("activity")}>
                {t("profile_page.tabs.activity")}
              </button>
            )}
            {/* Aba de Denúncias - só mostra no próprio perfil */}
            {isOwnProfile && (
              <button className={`tab-button ${activeTab === "reports" ? "active" : ""}`} onClick={() => setActiveTab("reports")}>
                {t("profile_page.tabs.reports", { count: userReports.length })}
              </button>
            )}
            {/* Aba de Estatísticas - só mostra no próprio perfil */}
            {isOwnProfile && (
              <button className={`tab-button ${activeTab === "stats" ? "active" : ""}`} onClick={() => setActiveTab("stats")}>
                {t("profile_page.tabs.stats")}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="profile-content">
        <div className="container">
          <div className="profile-content-wrapper">
            {/* Main Content */}
            <div className="profile-main-content">
              {activeTab === "overview" && (
                <div className="tab-content">
                  {/* Bio Section */}
                  <section className="content-section">
                    <h2 className="section-title">{t("profile_page.overview.about")}</h2>
                    <p className="bio-text">{bio}</p>
                  </section>

                  {/* Recent Votes */}
                  <section className="content-section">
                    <h2 className="section-title">{t("profile_page.overview.recent_votes")}</h2>
                    <div className="recent-votes-list">
                      {loading ? (
                        <p>{t("profile_page.overview.loading_votes")}</p>
                      ) : recentVotes.length > 0 ? (
                        recentVotes.map((vote, index) => (
                          <div key={index} className="vote-item">
                            <img
                              src={vote.botAvatar}
                              alt={vote.botName}
                              className="vote-bot-avatar"
                              style={{
                                width: "40px",
                                height: "40px",
                                borderRadius: "50%",
                              }}
                            />
                            <div className="vote-info">
                              <div className="vote-bot-name">{vote.botName}</div>
                              <div className="vote-date">{vote.date}</div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p>{isOwnProfile ? t("profile_page.overview.no_votes_own") : t("profile_page.overview.no_votes_user")}</p>
                      )}
                    </div>
                  </section>
                </div>
              )}

              {activeTab === "bots" && (
                <div className="tab-content">
                  <section className="content-section">
                    <div className="section-header-with-action">
                      <h2 className="section-title">{isOwnProfile ? t("profile_page.bots.my_bots_title") : t("profile_page.bots.title")}</h2>
                    </div>
                    <div className="my-bots-grid">
                      {loading ? (
                        <p>{t("profile_page.bots.loading")}</p>
                      ) : myBots.length > 0 ? (
                        myBots.map((bot) => (
                          <div key={bot.id} className={`my-bot-card ${bot.apiTier === "premium" ? "premium" : bot.apiTier === "business" ? "business" : ""}`}>
                            {bot.verified && <div className="verified-badge">{t("profile_page.bots.verified")}</div>}
                            {bot.status === "pending" && (
                              <div
                                className="status-badge"
                                style={{
                                  position: "absolute",
                                  top: "10px",
                                  right: "10px",
                                  background: "#FFA500",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {t("profile_page.bots.status.pending")}
                              </div>
                            )}
                            {bot.status === "rejected" && (
                              <div
                                className="status-badge"
                                style={{
                                  position: "absolute",
                                  top: "10px",
                                  right: "10px",
                                  background: "#FF4444",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {t("profile_page.bots.status.rejected")}
                              </div>
                            )}
                            {bot.status === "banned" && (
                              <div
                                className="status-badge"
                                style={{
                                  position: "absolute",
                                  top: "10px",
                                  right: "10px",
                                  background: "#FF4444",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {t("profile_page.bots.status.banned")}
                              </div>
                            )}
                            {bot.status === "reanalysis" && (
                              <div
                                className="status-badge"
                                style={{
                                  position: "absolute",
                                  top: "10px",
                                  right: "10px",
                                  background: "#FF9900",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {t("profile_page.bots.status.reanalysis")}
                              </div>
                            )}
                            <img
                              src={bot.avatar}
                              alt={bot.name}
                              className="bot-avatar-large"
                              style={{
                                width: "80px",
                                height: "80px",
                                borderRadius: "50%",
                                objectFit: "cover",
                                margin: "0 auto 10px",
                                display: "block",
                              }}
                            />
                            <h3 className="bot-name">
                              {bot.name}
                              {bot.apiTier === "premium" && <span className="tier-badge-inline premium">{t("profile_page.bots.tiers.premium")}</span>}
                              {bot.apiTier === "business" && <span className="tier-badge-inline business">{t("profile_page.bots.tiers.business")}</span>}
                            </h3>
                            <p className="bot-description" style={{ margin: "0", padding: "0" }}>
                              {bot.description}
                            </p>
                            <div className="bot-stats">
                              <div className="stat">
                                <span>👥</span>
                                <span>{bot.servers >= 1000 ? `${(bot.servers / 1000).toFixed(1)}K` : bot.servers}</span>
                              </div>
                              <div className="stat">
                                <span>🗳️</span>
                                <span>{bot.votes}</span>
                              </div>
                              <div className="stat">
                                <span>⭐</span>
                                <span>{bot.rating.toFixed(1)}</span>
                              </div>
                            </div>
                            <div className="bot-actions">
                              <Link to={`/bots/${bot.id}`} className="btn btn-outline btn-block btn-ver-detalhes">
                                {t("profile_page.bots.actions.details")}
                              </Link>
                              {isOwnProfile && (
                                <>
                                  {/* Aprovado: Editar + Excluir */}
                                  {bot.status === "approved" && (
                                    <>
                                      <Link to={`/edit-bot/${bot.id}`} className="btn btn-secondary btn-block">
                                        {t("profile_page.bots.actions.edit")}
                                      </Link>
                                      <button onClick={() => handleDeleteBot(bot)} className="btn btn-danger btn-block">
                                        {t("profile_page.bots.actions.delete")}
                                      </button>
                                    </>
                                  )}

                                  {/* Pendente: Nenhum botão adicional */}
                                  {bot.status === "pending" && null}

                                  {/* Reanálise: Nenhum botão adicional */}
                                  {bot.status === "reanalysis" && null}

                                  {/* Banido: Botão Motivo */}
                                  {bot.status === "banned" && (
                                    <button onClick={() => handleShowBanReason(bot)} className="btn btn-warning btn-block">
                                      {t("profile_page.bots.actions.reason")}
                                    </button>
                                  )}

                                  {/* Rejeitado: Editar + Motivo/Reanálise */}
                                  {bot.status === "rejected" && (
                                    <>
                                      <Link to={`/edit-bot/${bot.id}`} className="btn btn-secondary btn-block">
                                        {t("profile_page.bots.actions.edit")}
                                      </Link>
                                      <button onClick={() => handleShowRejectionReason(bot)} className="btn btn-warning btn-block">
                                        {t("profile_page.bots.actions.reason_reanalysis")}
                                      </button>
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <p>
                          {isOwnProfile ? t("profile_page.bots.empty.own") : t("profile_page.bots.empty.user")}
                          {isOwnProfile && <Link to="/add-bot">{t("profile_page.bots.empty.add_link")}</Link>}
                        </p>
                      )}
                    </div>
                  </section>
                </div>
              )}

              {activeTab === "activity" && (
                <div className="tab-content">
                  <section className="content-section">
                    <h2 className="section-title">{t("profile_page.activity.title")}</h2>
                    <div className="activity-timeline">
                      {loading ? (
                        <p>{t("profile_page.activity.loading")}</p>
                      ) : activities.length > 0 ? (
                        <>
                          {activities.slice(activityPage * 5, activityPage * 5 + 5).map((activity) => (
                            <div key={activity.id} className="activity-item">
                              <div className="activity-icon">{activity.description.split(" ")[0]}</div>
                              <div className="activity-content">
                                {activity.bot ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "10px",
                                    }}
                                  >
                                    <img
                                      src={activity.bot.avatar}
                                      alt={activity.bot.name}
                                      style={{
                                        width: "32px",
                                        height: "32px",
                                        borderRadius: "50%",
                                        objectFit: "cover",
                                      }}
                                    />
                                    <p>
                                      <strong>{activity.description.replace(activity.description.split(" ")[0], "").trim()}</strong> {activity.bot.name}
                                    </p>
                                  </div>
                                ) : (
                                  <p>
                                    <strong>{activity.description}</strong>
                                  </p>
                                )}
                                <span className="activity-time">{activity.time}</span>
                              </div>
                            </div>
                          ))}
                          {activities.length > 5 && (
                            <div className="activity-pagination">
                              <button className="pagination-btn" onClick={() => setActivityPage((p) => p - 1)} disabled={activityPage === 0}>
                                {t("profile_page.activity.pagination.prev")}
                              </button>
                              <span className="pagination-info">
                                {t("profile_page.activity.pagination.info", {
                                  current: activityPage + 1,
                                  total: Math.ceil(activities.length / 5),
                                })}
                              </span>
                              <button className="pagination-btn" onClick={() => setActivityPage((p) => p + 1)} disabled={activityPage >= Math.ceil(activities.length / 5) - 1}>
                                {t("profile_page.activity.pagination.next")}
                              </button>
                            </div>
                          )}
                        </>
                      ) : (
                        <p>{t("profile_page.activity.empty")}</p>
                      )}
                    </div>
                  </section>
                </div>
              )}

              {activeTab === "reports" && (
                <div className="tab-content">
                  <section className="content-section">
                    <h2 className="section-title">{t("profile_page.reports.title")}</h2>
                    <div className="reports-list">
                      {loading ? (
                        <p>{t("profile_page.reports.loading")}</p>
                      ) : userReports.length > 0 ? (
                        <>
                          {userReports.slice(reportsPage * 5, reportsPage * 5 + 5).map((report) => (
                            <div key={report.id} className="user-report-card">
                              <div className="report-card-header">
                                <div className="report-target">
                                  <img src={report.target?.avatar} alt={report.target?.name || report.target?.username} className="target-avatar" />
                                  <div className="target-info">
                                    <span className="target-type-badge">
                                      {report.targetType === "bot" ? t("profile_page.reports.target_type.bot") : t("profile_page.reports.target_type.user")}
                                    </span>
                                    <h3 className="target-name">{report.target?.name || report.target?.username}</h3>
                                  </div>
                                </div>
                                <div className={`report-status status-${report.status}`}>
                                  {report.status === "pending" && t("profile_page.reports.status.pending")}
                                  {report.status === "investigating" && t("profile_page.reports.status.investigating")}
                                  {report.status === "resolved" && t("profile_page.reports.status.resolved")}
                                  {report.status === "rejected" && t("profile_page.reports.status.rejected")}
                                </div>
                              </div>
                              <div className="report-card-body">
                                <div className="report-reason">
                                  <strong>{t("profile_page.reports.labels.reason")}</strong> {report.reason}
                                </div>
                                <div className="report-description">
                                  <strong>{t("profile_page.reports.labels.description")}</strong>
                                  <p>{report.description}</p>
                                </div>
                                {report.adminNotes && (
                                  <div className="report-admin-notes">
                                    <strong>{t("profile_page.reports.labels.admin_notes")}</strong>
                                    <p>{report.adminNotes}</p>
                                  </div>
                                )}
                              </div>
                              <div className="report-card-footer">
                                <span className="report-date">
                                  {t("profile_page.reports.labels.sent_at", {
                                    date: new Date(report.createdAt).toLocaleDateString(i18n.language === "pt" ? "pt-BR" : "en-US", {
                                      day: "2-digit",
                                      month: "2-digit",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    }),
                                  })}
                                </span>
                              </div>
                            </div>
                          ))}
                          {userReports.length > 5 && (
                            <div className="activity-pagination">
                              <button className="pagination-btn" onClick={() => setReportsPage((p) => p - 1)} disabled={reportsPage === 0}>
                                {t("profile_page.reports.pagination.prev")}
                              </button>
                              <span className="pagination-info">
                                {t("profile_page.reports.pagination.info", {
                                  current: reportsPage + 1,
                                  total: Math.ceil(userReports.length / 5),
                                })}
                              </span>
                              <button className="pagination-btn" onClick={() => setReportsPage((p) => p + 1)} disabled={reportsPage >= Math.ceil(userReports.length / 5) - 1}>
                                {t("profile_page.reports.pagination.next")}
                              </button>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="empty-reports">
                          <div className="empty-reports-icon">🚩</div>
                          <p>{t("profile_page.reports.empty")}</p>
                        </div>
                      )}
                    </div>
                  </section>
                </div>
              )}

              {activeTab === "stats" && (
                <div className="tab-content">
                  <section className="content-section">
                    <div className="stats-header-section">
                      <div className="stats-title-block">
                        <h2 className="section-title">{t("profile_page.stats.title")}</h2>
                        <p className="section-subtitle">{t("profile_page.stats.subtitle")}</p>
                      </div>

                      {myBots.length > 0 && (
                        <div className="bot-selection-container">
                          <label className="selection-label">{t("profile_page.stats.select_bot")}</label>
                          <div className="bot-selector-mini">
                            {myBots.map((bot) => (
                              <button key={bot.id} className={`mini-bot-btn ${selectedBotId === bot.id ? "active" : ""}`} onClick={() => setSelectedBotId(bot.id)} title={bot.name}>
                                {selectedBotId === bot.id && <span className="active-dot"></span>}
                                <img src={bot.avatar} alt={bot.name} />
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {!selectedBotId ? (
                      <div className="empty-stats">
                        <div className="empty-stats-icon">📊</div>
                        <p>{myBots.length > 0 ? t("profile_page.stats.empty_select") : t("profile_page.stats.empty_no_bots")}</p>
                      </div>
                    ) : statsLoading && !apiUsage ? (
                      <p>{t("profile_page.stats.loading")}</p>
                    ) : apiUsage ? (
                      <div className="stats-container">
                        {/* Summary Cards */}
                        <div className="api-stats-grid">
                          <div className="api-stat-card">
                            <span className="api-stat-label">{t("profile_page.stats.cards.plan")}</span>
                            <div className="api-stat-value-row">
                              <span className={`tier-badge ${apiUsage.tier?.name || "free"}`}>{apiUsage.tier?.displayName || "FREE"}</span>
                            </div>
                            <span className="api-stat-subtext">
                              {apiUsage.tier?.name === "business"
                                ? `100.000 ${t("profile_page.stats.cards.calls_unit")}`
                                : apiUsage.tier?.name === "premium"
                                  ? `10.000 ${t("profile_page.stats.cards.calls_unit")}`
                                  : `1.500 ${t("profile_page.stats.cards.calls_unit")}`}
                            </span>
                          </div>

                          <div className="api-stat-card">
                            <span className="api-stat-label">{t("profile_page.stats.cards.usage")}</span>
                            <div className="api-stat-value">
                              {apiUsage.usage?.apiCalls?.used?.toLocaleString() || 0} <small>/ {apiUsage.usage?.apiCalls?.limit?.toLocaleString() || 0}</small>
                            </div>
                            <div className="usage-bar-wrapper">
                              <div
                                className="usage-bar-fill"
                                style={{
                                  width: `${Math.min(apiUsage.usage?.apiCalls?.percentage || 0, 100)}%`,
                                }}
                              ></div>
                            </div>
                            <span className="api-stat-subtext">
                              {t("profile_page.stats.cards.used_of_limit", {
                                percentage: apiUsage.usage?.apiCalls?.percentage || 0,
                              })}
                            </span>
                          </div>

                          {/* Métricas Avançadas para Premium/Business */}
                          {(apiUsage.tier?.name === "premium" || apiUsage.tier?.name === "business") && (
                            <>
                              <div className="api-stat-card">
                                <span className="api-stat-label">{t("profile_page.stats.cards.avg_time")}</span>
                                <div className="api-stat-value">
                                  {apiUsage.statistics?.usageHistory?.[0]?.avgResponseTime || 0}
                                  <small>ms</small>
                                </div>
                                <span className="api-stat-subtext">{t("profile_page.stats.cards.based_on_last_day")}</span>
                              </div>
                              <div className="api-stat-card">
                                <span className="api-stat-label">{t("profile_page.stats.cards.endpoints")}</span>
                                <div className="api-stat-value">{apiUsage.statistics?.usageHistory?.[0]?.uniqueEndpoints || 0}</div>
                                <span className="api-stat-subtext">{t("profile_page.stats.cards.unique_today")}</span>
                              </div>
                            </>
                          )}

                          {apiUsage.tier?.name === "business" && (
                            <div className="api-stat-card">
                              <span className="api-stat-label">{t("profile_page.stats.cards.success_rate")}</span>
                              <div className="api-stat-value">
                                {apiUsage.statistics?.usageHistory?.[0]?.calls > 0
                                  ? Math.round((apiUsage.statistics.usageHistory[0].successCalls / apiUsage.statistics.usageHistory[0].calls) * 100)
                                  : 100}
                                %
                              </div>
                              <span className="api-stat-subtext">{t("profile_page.stats.cards.uptime_today")}</span>
                            </div>
                          )}

                          {apiUsage.tier?.name === "free" && (
                            <div className="api-stat-card">
                              <span className="api-stat-label">{t("profile_page.stats.cards.next_reset")}</span>
                              <div className="api-stat-value">
                                {apiUsage.usage?.reset?.inDays || 0}{" "}
                                {apiUsage.usage?.reset?.inDays === 1 ? t("profile_page.stats.cards.reset_suffix_one") : t("profile_page.stats.cards.reset_suffix_other")}
                              </div>
                              <span className="api-stat-subtext">
                                {apiUsage.usage?.reset?.at &&
                                  t("profile_page.stats.cards.reset_at", {
                                    date: new Date(apiUsage.usage.reset.at).toLocaleDateString(i18n.language === "pt" ? "pt-BR" : "en-US"),
                                  })}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* History Chart Section */}
                        <div className="stats-history-section">
                          <div className="chart-header">
                            <h3 className="stats-subtitle">
                              {t("profile_page.stats.history.title", {
                                days: apiUsage.statistics?.usageHistory?.length || 0,
                              })}
                            </h3>
                            {(apiUsage.tier?.name === "premium" || apiUsage.tier?.name === "business") && (
                              <div className="chart-toggles">
                                <button className={`chart-toggle-btn ${chartMode === "bar" ? "active" : ""}`} onClick={() => setChartMode("bar")}>
                                  {t("profile_page.stats.history.mode_bar")}
                                </button>
                                <button className={`chart-toggle-btn ${chartMode === "line" ? "active" : ""}`} onClick={() => setChartMode("line")}>
                                  {t("profile_page.stats.history.mode_line")}
                                </button>
                              </div>
                            )}
                          </div>

                          <div className="history-chart">
                            {apiUsage.statistics?.usageHistory && apiUsage.statistics.usageHistory.length > 0 ? (
                              <StatsChart data={apiUsage.statistics.usageHistory} type={chartMode} />
                            ) : (
                              <p className="no-data">{t("profile_page.stats.history.no_data")}</p>
                            )}
                          </div>
                        </div>

                        {/* Recent Logs Table */}
                        <div className="logs-section">
                          <h3 className="stats-subtitle">{t("profile_page.stats.logs.title")}</h3>
                          <div className="logs-table-wrapper">
                            <table className="logs-table">
                              <thead>
                                <tr>
                                  <th>{t("profile_page.stats.logs.table.method")}</th>
                                  <th>{t("profile_page.stats.logs.table.endpoint")}</th>
                                  <th>{t("profile_page.stats.logs.table.status")}</th>
                                  <th>{t("profile_page.stats.logs.table.response")}</th>
                                  <th>{t("profile_page.stats.logs.table.datetime")}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {apiUsage.logs?.data && apiUsage.logs.data.length > 0 ? (
                                  apiUsage.logs.data.map((log) => (
                                    <tr key={log.id}>
                                      <td>
                                        <span className={`method-badge ${log.method?.toLowerCase()}`}>{log.method}</span>
                                      </td>
                                      <td className="endpoint-cell">{log.endpoint}</td>
                                      <td>
                                        <span className={`status-badge ${log.status_code >= 200 && log.status_code < 300 ? "success" : "error"}`}>{log.status_code}</span>
                                      </td>
                                      <td>{log.response_time_ms}ms</td>
                                      <td>
                                        {new Date(log.created_at).toLocaleString(i18n.language === "pt" ? "pt-BR" : "en-US", {
                                          day: "2-digit",
                                          month: "2-digit",
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        })}
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan="5" className="no-logs">
                                      {t("profile_page.stats.logs.no_logs")}
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>

                          {apiUsage.logs?.pagination?.total > 5 && (
                            <div className="activity-pagination">
                              <button className="pagination-btn" onClick={() => setUsagePage((p) => p - 1)} disabled={usagePage === 1}>
                                {t("profile_page.stats.logs.pagination.prev")}
                              </button>
                              <span className="pagination-info">
                                {t("profile_page.stats.logs.pagination.info", {
                                  current: usagePage,
                                  total: Math.ceil(apiUsage.logs.pagination.total / 5),
                                })}
                              </span>
                              <button className="pagination-btn" onClick={() => setUsagePage((p) => p + 1)} disabled={usagePage >= Math.ceil(apiUsage.logs.pagination.total / 5)}>
                                {t("profile_page.stats.logs.pagination.next")}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </section>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <aside className="profile-sidebar">
              {/* Quick Actions - apenas se for o próprio perfil */}
              {isOwnProfile && (
                <div className="sidebar-card">
                  <h3 className="sidebar-title">{t("profile_page.sidebar.quick_actions.title")}</h3>
                  <div className="quick-actions">
                    <Link to="/add-bot" className="quick-action-btn">
                      <span>➕</span> {t("profile_page.sidebar.quick_actions.add_bot")}
                    </Link>
                    <Link to="/bots" className="quick-action-btn">
                      <span>🔍</span> {t("profile_page.sidebar.quick_actions.explore_bots")}
                    </Link>
                    <Link to="/settings" className="quick-action-btn">
                      <span>⚙️</span> {t("profile_page.sidebar.quick_actions.settings")}
                    </Link>
                  </div>
                </div>
              )}

              {/* Account Info */}
              <div className="sidebar-card">
                <h3 className="sidebar-title">{t("profile_page.sidebar.account_info.title")}</h3>
                <div className="account-info-list">
                  <div className="info-item">
                    <span className="info-label">{t("profile_page.sidebar.account_info.username")}</span>
                    <span className="info-value">{user.username}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">{t("profile_page.sidebar.account_info.discord_id")}</span>
                    <span className="info-value" style={{ fontSize: "0.85rem" }}>
                      {user.id}
                    </span>
                  </div>
                  {/* <div className="info-item">
                                        <span className="info-label">Conta verificada:</span>
                                        <span className="info-value">
                                            {userProfile?.verified ? '✅ Sim' : '❌ Não'}
                                        </span>
                                    </div> */}
                  <div className="info-item">
                    <span className="info-label">{t("profile_page.sidebar.account_info.member_since")}</span>
                    <span className="info-value">{stats.memberSince}</span>
                  </div>
                </div>
              </div>

              {/* Support - Only show if not own profile */}
              {!isOwnProfile && (
                <div className="sidebar-card">
                  <h3 className="sidebar-title">{t("profile_page.sidebar.support.title")}</h3>
                  <p className="sidebar-text">{t("profile_page.sidebar.support.desc")}</p>
                  <button className="btn btn-danger btn-block btn-denunciar-usuario" onClick={() => setShowReportModal(true)}>
                    {t("profile_page.sidebar.support.btn_report")}
                  </button>
                </div>
              )}
            </aside>
          </div>
        </div>
      </div>

      {/* Validation/Error Modal */}
      <SuccessModal isOpen={showSuccessModal} onClose={handleCloseSuccessModal} title={modalConfig.title} message={modalConfig.message} />

      {/* Report Modal */}
      {showReportModal && (
        <div className="modal-overlay" onClick={() => setShowReportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t("profile_page.modals.report_user.title")}</h2>
              <button className="modal-close" onClick={() => setShowReportModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="reportReason">{t("profile_page.modals.report_user.reason_label")}</label>
                <select id="reportReason" className="form-control" value={reportReason} onChange={(e) => setReportReason(e.target.value)}>
                  <option value="">{t("profile_page.modals.report_user.reason_placeholder")}</option>
                  <option value="Spam">{t("profile_page.modals.report_user.reasons.spam")}</option>
                  <option value="Assédio">{t("profile_page.modals.report_user.reasons.harassment")}</option>
                  <option value="Comportamento Abusivo">{t("profile_page.modals.report_user.reasons.abusive")}</option>
                  <option value="Conteúdo Inapropriado">{t("profile_page.modals.report_user.reasons.inappropriate")}</option>
                  <option value="Perfil Falso">{t("profile_page.modals.report_user.reasons.fake")}</option>
                  <option value="Violação de Termos">{t("profile_page.modals.report_user.reasons.terms")}</option>
                  <option value="Outro">{t("profile_page.modals.report_user.reasons.other")}</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="reportDescription">{t("profile_page.modals.report_user.desc_label")}</label>
                <textarea
                  id="reportDescription"
                  className="form-control"
                  rows="5"
                  placeholder={t("profile_page.modals.report_user.desc_placeholder")}
                  value={reportDescription}
                  onChange={(e) => setReportDescription(e.target.value)}
                  maxLength={1000}
                />
                <small className="form-text">
                  {t("profile_page.modals.report_user.char_count", {
                    count: reportDescription.length,
                  })}
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowReportModal(false)}>
                {t("profile_page.modals.report_user.btns.cancel")}
              </button>
              <button className="btn btn-danger btn-enviar-denuncia" onClick={handleSubmitReport}>
                {t("profile_page.modals.report_user.btns.send")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Exclusão de Bot */}
      {showDeleteBotModal && botToDelete && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{t("profile_page.modals.delete_bot.title")}</h2>
            </div>
            <div className="modal-body">
              <p>
                <Trans i18nKey="profile_page.modals.delete_bot.question" values={{ name: botToDelete.name }}>
                  Tem certeza que deseja excluir o bot <strong>{botToDelete.name}</strong>?
                </Trans>
              </p>
              <p className="modal-warning">
                <Trans i18nKey="profile_page.modals.delete_bot.warning">
                  ⚠️ Esta ação é <strong>IRREVERSÍVEL</strong>! Todos os dados do bot serão permanentemente excluídos, incluindo:
                </Trans>
              </p>
              <ul
                style={{
                  textAlign: "left",
                  marginLeft: "20px",
                  color: "var(--text-secondary)",
                }}
              >
                <li>{t("profile_page.modals.delete_bot.items.stats")}</li>
                <li>{t("profile_page.modals.delete_bot.items.votes")}</li>
                <li>{t("profile_page.modals.delete_bot.items.reviews")}</li>
                <li>{t("profile_page.modals.delete_bot.items.config")}</li>
              </ul>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={cancelDeleteBot}>
                {t("profile_page.modals.delete_bot.btns.cancel")}
              </button>
              <button className="btn btn-danger" onClick={confirmDeleteBot}>
                {t("profile_page.modals.delete_bot.btns.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Ação Administrativa (Banimento/Rejeição) */}
      <AdminActionModal isOpen={showAdminActionModal} onClose={() => setShowAdminActionModal(false)} action={adminActionData} type={adminActionType} />
    </div>
  );
}

export default ProfilePage;
